import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  ReactFlowProvider,
  Node,
  useNodesState,
  useEdgesState,
  BackgroundVariant,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import {
  Loader,
  CheckCircle,
  XCircle,
  Calendar,
  MousePointerClick,
  Layout,
  ChevronDown,
  ChevronUp,
  Clock,
  AlertTriangle,
  Zap,
  FileText,
  FileUp,
  FileDown,
  Cpu,
  BarChart3,
  ArrowUpRight,
  ArrowDownRight,
  Sparkles,
  Info,
  Layers,
} from "lucide-react";
import {
  processImportedNodes,
  processInitialEdges,
  injectTemplateIdToHttpNodes,
} from "../../utils/workflowHelpers";
import { nodeTypes } from "../../nodes";
import { DeleteButtonEdge } from "../../edge/deleteEdge";
import JsonViewerContainer from "../ui/JsonViewer";
import apiClient from "../../../api/api";
import { API_ENDPOINTS } from "../../../api/api.config";
import ExecutionTokenAnalytics from "./ExecutionTokenAnalytics";
import { ExecutionTimeline } from "./ExecutionTimeline";

interface ExecutionsPanelProps {
  nodes?: Node<any>[];
  isRunning?: boolean;
  executionId?: string | null;
  templateId?: string;
  refreshTrigger?: number;
  workflowName?: string;
}

const edgeTypes = {
  deleteButtonEdge: DeleteButtonEdge,
};

export const ExecutionsPanel: React.FC<ExecutionsPanelProps> = ({
  nodes: parentNodes,
  templateId,
  refreshTrigger,
  workflowName,
}) => {
  const [executions, setExecutions] = useState<any[]>([]);
  const [selectedExecutionId, setSelectedExecutionId] = useState<string | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [showTokenAnalytics, setShowTokenAnalytics] = useState(false);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [templateNodes, setTemplateNodes] = useState<Node<any>[]>([]);
  const [templateEdges, setTemplateEdges] = useState<any[]>([]);
  // Ref for parent nodes so it doesn't trigger re-renders
  const parentNodesRef = useRef<Node<any>[]>([]);
  const [selectedNode, setSelectedNode] = useState<Node<any> | null>(null);
  const [, setSelectedStepId] = useState<string | null>(null);
  const [isPanelExpanded, setIsPanelExpanded] = useState(false);
  const [selectedIterationIndex, setSelectedIterationIndex] =
    useState<number>(0);

  // Tree-based execution tracking state
  const [expandedLoops, setExpandedLoops] = useState<Record<string, boolean>>(
    {},
  );
  const [selectedExecutionNodeId, setSelectedExecutionNodeId] = useState<
    string | null
  >(null);

  // Resize Panel State
  const [panelHeight, setPanelHeight] = useState(450);
  const [isResizing, setIsResizing] = useState(false);

  // Pagination State
  const [currentPage, setCurrentPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const LIMIT = 20;

  // New State for Panel Tabs
  const [activeTab, setActiveTab] = useState<
    "inputs" | "outputs" | "logs" | "error" | "usage" | "overview"
  >("outputs");

  // Polling ref for IN_PROGRESS executions
  const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null,
  );
  const selectedExecutionPollingRef = useRef<ReturnType<
    typeof setInterval
  > | null>(null);
  const POLLING_INTERVAL = 2000; // Poll every 2 seconds

  // Keep parent nodes ref in sync
  parentNodesRef.current = parentNodes || [];

  // Fetch template nodes/edges once for rendering the canvas
  useEffect(() => {
    if (!templateId) return;

    const fetchTemplate = async () => {
      try {
        const response = await apiClient.get(
          `${API_ENDPOINTS.WORKFLOW.WORKFLOW_CRUD}/${templateId}`,
        );
        if (response.data?.success && response.data?.data) {
          const workflowData = response.data.data;
          if (workflowData.nodes && Array.isArray(workflowData.nodes)) {
            const processed = processImportedNodes(
              workflowData.nodes,
              () => {},
            );
            setTemplateNodes(processed);
          }
          if (workflowData.edges && Array.isArray(workflowData.edges)) {
            setTemplateEdges(processInitialEdges(workflowData.edges));
          }
        }
      } catch (error) {
        console.error("Failed to fetch template:", error);
      }
    };

    fetchTemplate();
  }, [templateId]);

  // Fetch executions when templateId or page changes
  useEffect(() => {
    const fetchExecutions = async () => {
      setIsLoading(true);
      try {
        const skip = currentPage * LIMIT;
        const response = await apiClient.post(
          API_ENDPOINTS.WORKFLOW.EXECUTION_HEADERS,
          {
            template_id: templateId,
            skip: skip,
            limit: LIMIT,
          },
        );
        if (response.data?.success && response.data?.data?.executions) {
          const sortedExecutions = response.data.data.executions.sort(
            (a: any, b: any) =>
              new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
          );
          setExecutions(sortedExecutions);

          // Set total count if available from API response
          if (response.data?.data?.total !== undefined) {
            setTotalCount(response.data.data.total);
          } else if (response.data?.data?.total_count !== undefined) {
            setTotalCount(response.data.data.total_count);
          }
        }
      } catch (error) {
        console.error("Failed to fetch executions:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (templateId) {
      fetchExecutions();
    }
  }, [templateId, currentPage, refreshTrigger]);

  // Poll for IN_PROGRESS executions
  useEffect(() => {
    if (!templateId) return;

    const pollInProgressExecutions = async () => {
      setExecutions((prevExecutions) => {
        const inProgressIds = prevExecutions
          .filter((exec) => exec.overall_proccessing_status === "IN_PROGRESS")
          .map((exec) => exec._id);

        if (inProgressIds.length === 0) {
          // No IN_PROGRESS executions, clear the interval
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          return prevExecutions;
        }

        // Perform the API call outside setState
        apiClient
          .post(API_ENDPOINTS.WORKFLOW.EXECUTION_STATUS_BY_IDS, {
            template_id: templateId,
            execution_ids: inProgressIds,
          })
          .then((response) => {
            if (response.data?.success && Array.isArray(response.data?.data)) {
              const statusUpdates = response.data.data;

              setExecutions((prev) =>
                prev.map((exec) => {
                  const statusUpdate = statusUpdates.find(
                    (update: any) => update._id === exec._id,
                  );
                  if (statusUpdate) {
                    return {
                      ...exec,
                      overall_proccessing_status:
                        statusUpdate.overall_proccessing_status,
                      overall_proccessing: {
                        ...exec.overall_proccessing,
                        ...statusUpdate.overall_proccessing,
                      },
                    };
                  }
                  return exec;
                }),
              );
            }
          })
          .catch((error) => {
            console.error("Failed to poll execution status:", error);
          });

        return prevExecutions;
      });
    };

    // Start polling interval
    pollingIntervalRef.current = setInterval(
      pollInProgressExecutions,
      POLLING_INTERVAL,
    );

    // Cleanup on unmount
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [templateId]);

  // Fetch full execution data when an execution is selected
  const [fullExecutionData, setFullExecutionData] = useState<any>(null);
  const [isLoadingExecution, setIsLoadingExecution] = useState(false);

  useEffect(() => {
    if (!selectedExecutionId) {
      setFullExecutionData(null);
      return;
    }

    // Clear stale data immediately so the right side resets
    setFullExecutionData(null);

    const fetchFullExecution = async () => {
      setIsLoadingExecution(true);
      try {
        const response = await apiClient.get(
          API_ENDPOINTS.WORKFLOW.EXECUTION_GET_BY_ID(selectedExecutionId),
        );
        if (response.data?.success && response.data?.data) {
          setFullExecutionData(response.data.data);
          // Also update the executions list with fresh data
          setExecutions((prev) =>
            prev.map((exec) =>
              exec._id === selectedExecutionId
                ? { ...exec, ...response.data.data }
                : exec,
            ),
          );
        }
      } catch (error) {
        console.error("Failed to fetch full execution data:", error);
      } finally {
        setIsLoadingExecution(false);
      }
    };

    fetchFullExecution();
  }, [selectedExecutionId]);

  // Handle selection of an execution
  useEffect(() => {
    if (!selectedExecutionId || !fullExecutionData) return;

    const execution = fullExecutionData;

    console.log(
      "[ExecutionsPanel] Processing execution:",
      selectedExecutionId,
      {
        hasNodes: !!execution.nodes,
        nodeCount: execution.nodes?.length,
        hasNodeStatus: !!execution.node_status,
        nodeStatusCount: execution.node_status?.length,
        overallStatus: execution.overall_proccessing_status,
        templateNodesCount: templateNodes.length,
        parentNodesCount: parentNodes?.length,
      },
    );

    // Use execution.nodes if available, then template nodes, then parent nodes as fallback
    let sourceNodes: Node<any>[] = [];
    let nodeSource = "none";
    if (
      execution.nodes &&
      Array.isArray(execution.nodes) &&
      execution.nodes.length > 0
    ) {
      // Filter out re-run entries to avoid duplicate ReactFlow nodes on the canvas
      const seenIds = new Set<string>();
      const uniqueNodes = execution.nodes.filter((n: any) => {
        if (seenIds.has(n.id)) return false;
        seenIds.add(n.id);
        return true;
      });
      sourceNodes = processImportedNodes(uniqueNodes, () => {});
      nodeSource = "execution";
    } else if (templateNodes.length > 0) {
      sourceNodes = templateNodes;
      nodeSource = "template";
    } else if (parentNodesRef.current.length > 0) {
      // Use parent nodes as last resort fallback
      sourceNodes = parentNodesRef.current.map((n) => ({
        ...n,
        data: { ...n.data },
      }));
      nodeSource = "parent";
    }
    console.log(
      "[ExecutionsPanel] Using",
      nodeSource,
      "nodes, count:",
      sourceNodes.length,
    );

    if (sourceNodes.length > 0) {
      // Map status to nodes
      // Track which node_status entries have been used to avoid duplicate assignments
      const usedStatusIds = new Set<string>();

      const nodesWithStatus = sourceNodes.map((node) => {
        // Strategy 1: Exact match by name or id
        let nodeStatus = execution.node_status?.find(
          (ns: any) =>
            !usedStatusIds.has(ns.id) &&
            (ns.node_name === node.data.name || ns.node_name === node.id),
        );

        // Strategy 2: Match by prefix (e.g., "HttpAPINode" matches "HttpAPINode1")
        // Combined with index to pick the correct one
        if (!nodeStatus && node.data.index) {
          const nodeIndex = node.data.index;
          nodeStatus = execution.node_status?.find(
            (ns: any, idx: number) =>
              !usedStatusIds.has(ns.id) &&
              node.data.name?.startsWith(ns.node_name) &&
              idx + 1 === nodeIndex,
          );
        }

        // Strategy 3: Match by prefix without index
        if (!nodeStatus) {
          nodeStatus = execution.node_status?.find(
            (ns: any) =>
              !usedStatusIds.has(ns.id) &&
              node.data.name?.startsWith(ns.node_name),
          );
        }

        // Mark this status as used
        if (nodeStatus?.id) {
          usedStatusIds.add(nodeStatus.id);
        }

        let executionStatus = null;
        if (nodeStatus) {
          const status = nodeStatus.status;
          if (
            ["RUNNING", "PENDING", "PROCESSING", "IN_PROGRESS"].includes(status)
          ) {
            executionStatus = "running";
          } else if (status === "DONE") {
            executionStatus = "success";
          } else if (["FAILED", "ERROR"].includes(status)) {
            executionStatus = "error";
          }
        } else if (execution.overall_proccessing_status === "IN_PROGRESS") {
          // Node has no status yet but execution is in progress
          const nodeIndex = node.data.index || 0;
          const completedNodes =
            execution.node_status?.filter((ns: any) => ns.status === "DONE")
              .length || 0;

          if (
            completedNodes === nodeIndex - 1 ||
            completedNodes >= nodeIndex - 1
          ) {
            const allNodeIds = sourceNodes.map((n) => n.id);
            const doneNodeNames =
              execution.node_status
                ?.filter((ns: any) => ns.status === "DONE")
                .map((ns: any) => ns.node_name) || [];

            const nodeOrderIndex = allNodeIds.indexOf(node.id);
            const isFirstPendingNode =
              !doneNodeNames.includes(node.data.name) &&
              !doneNodeNames.includes(node.id) &&
              allNodeIds.slice(0, nodeOrderIndex).every((id) => {
                const n = sourceNodes.find((pn) => pn.id === id);
                return (
                  doneNodeNames.includes(n?.data.name) ||
                  doneNodeNames.includes(id)
                );
              });

            if (isFirstPendingNode) {
              executionStatus = "running";
            }
          }
        }

        // Check for top-level error if status is not already set or not error
        const errorObj =
          execution.error || execution.overall_proccessing?.error;
        if (
          errorObj &&
          (errorObj.node_name === node.data.name ||
            errorObj.node_name === node.id)
        ) {
          executionStatus = "error";
        }

        return {
          ...node,
          draggable: false, // Read-only
          connectable: false, // Read-only
          data: {
            ...node.data,
            executionStatus,
          },
        };
      });

      console.log(
        "[ExecutionsPanel] Node statuses:",
        nodesWithStatus.map((n) => ({
          id: n.id,
          name: n.data.name,
          executionStatus: n.data.executionStatus,
        })),
      );

      // Inject template ID if needed
      const finalNodes = templateId
        ? injectTemplateIdToHttpNodes(nodesWithStatus, templateId)
        : nodesWithStatus;

      setNodes(finalNodes);
    }

    // Use execution.edges if available, otherwise fall back to template edges or parent edges
    let sourceEdges: any[] = [];
    if (
      execution.edges &&
      Array.isArray(execution.edges) &&
      execution.edges.length > 0
    ) {
      sourceEdges = processInitialEdges(execution.edges);
    } else if (templateEdges.length > 0) {
      sourceEdges = templateEdges;
    }

    if (sourceEdges.length > 0) {
      setEdges(
        sourceEdges.map((edge) => ({
          ...edge,
          animated: false, // Static view
          data: { ...edge.data, locked: true },
        })),
      );
    }
  }, [
    selectedExecutionId,
    fullExecutionData,
    templateId,
    templateNodes,
    templateEdges,
    setNodes,
    setEdges,
  ]);

  // Reset selected node specific logic when switching executions
  useEffect(() => {
    setSelectedNode(null);
    setSelectedStepId(null);
    setIsPanelExpanded(false);
    setSelectedExecutionNodeId(null);
    setExpandedLoops({});
    setSelectedIterationIndex(0);
  }, [selectedExecutionId]);

  // Poll for selected IN_PROGRESS execution to update node statuses in real-time
  useEffect(() => {
    if (!selectedExecutionId) {
      if (selectedExecutionPollingRef.current) {
        clearInterval(selectedExecutionPollingRef.current);
        selectedExecutionPollingRef.current = null;
      }
      return;
    }

    const selectedExecution = executions.find(
      (ex) => ex._id === selectedExecutionId,
    );

    // Only poll if the selected execution is IN_PROGRESS
    if (selectedExecution?.overall_proccessing_status !== "IN_PROGRESS") {
      if (selectedExecutionPollingRef.current) {
        clearInterval(selectedExecutionPollingRef.current);
        selectedExecutionPollingRef.current = null;
      }
      return;
    }

    const pollSelectedExecution = async () => {
      try {
        const response = await apiClient.get(
          API_ENDPOINTS.WORKFLOW.EXECUTION_GET_BY_ID(selectedExecutionId),
        );

        if (response.data?.success && response.data?.data) {
          const executionData = response.data.data;

          // Update full execution data for the bottom panel
          setFullExecutionData(executionData);

          // Update the executions array with fresh data
          setExecutions((prev) =>
            prev.map((exec) =>
              exec._id === selectedExecutionId
                ? { ...exec, ...executionData }
                : exec,
            ),
          );
        }
      } catch (error) {
        console.error("Failed to poll selected execution:", error);
      }
    };

    // Start polling for selected execution
    pollSelectedExecution(); // Initial poll
    selectedExecutionPollingRef.current = setInterval(
      pollSelectedExecution,
      POLLING_INTERVAL,
    );

    return () => {
      if (selectedExecutionPollingRef.current) {
        clearInterval(selectedExecutionPollingRef.current);
        selectedExecutionPollingRef.current = null;
      }
    };
  }, [
    selectedExecutionId,
    executions.find((ex) => ex._id === selectedExecutionId)
      ?.overall_proccessing_status,
  ]);

  // Format date helper
  const formatDate = (dateString: string) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    }).format(date);
  };

  const formatTokenCount = (count: number) => {
    if (count >= 1_000_000) return `${(count / 1_000_000).toFixed(2)}M`;
    if (count >= 1_000) return `${(count / 1_000).toFixed(1)}K`;
    return count.toLocaleString("en-IN");
  };

  // Get token usage for current execution
  const getTokenUsage = useCallback(() => {
    if (!fullExecutionData?.token_usages) return null;
    const usages = fullExecutionData.token_usages as any[];
    if (!usages.length) return null;

    const totals = usages.reduce(
      (acc: any, u: any) => ({
        prompt_tokens: acc.prompt_tokens + (u.prompt_tokens || 0),
        completion_tokens: acc.completion_tokens + (u.completion_tokens || 0),
        total_tokens: acc.total_tokens + (u.total_tokens || 0),
      }),
      { prompt_tokens: 0, completion_tokens: 0, total_tokens: 0 },
    );

    return { perNode: usages, totals };
  }, [fullExecutionData]);

  // Status icon helper
  const getStatusIcon = (status: string) => {
    switch (status) {
      case "running":
        return (
          <div className="w-5 h-5 rounded-full bg-blue-50 border border-blue-200 flex items-center justify-center">
            <Loader className="w-3 h-3 text-blue-500 animate-spin" />
          </div>
        );
      case "success":
      case "DONE":
        return (
          <div className="w-5 h-5 rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center">
            <CheckCircle className="w-3.5 h-3.5 text-emerald-500" />
          </div>
        );
      case "error":
      case "FAILED":
        return (
          <div className="w-5 h-5 rounded-full bg-rose-50 border border-rose-200 flex items-center justify-center">
            <XCircle className="w-3.5 h-3.5 text-rose-500" />
          </div>
        );
      case "IN_PROGRESS":
      case "RUNNING":
      case "PENDING":
      case "PROCESSING":
        return (
          <div className="w-5 h-5 rounded-full bg-blue-50 border border-blue-200 flex items-center justify-center">
            <Loader className="w-3 h-3 text-blue-500 animate-spin" />
          </div>
        );
      default:
        return (
          <div className="w-5 h-5 rounded-full bg-gray-50 border border-gray-200 flex items-center justify-center">
            <MousePointerClick className="w-3 h-3 text-gray-400" />
          </div>
        );
    }
  };

  const onNodeClick = useCallback(
    (_: React.MouseEvent, node: Node) => {
      setSelectedNode(node);
      setSelectedStepId(null); // Clear specific step selection when clicking generic node
      setIsPanelExpanded(true);

      // In tree mode, find the first execution_node_id matching this ReactFlow node
      if (fullExecutionData?.execution_tree) {
        const matchingStatus = fullExecutionData.node_status?.find((s: any) => {
          const matchingNode = fullExecutionData.nodes?.find(
            (n: any) => n.execution_node_id === s.execution_node_id,
          );
          return matchingNode?.id === node.id;
        });
        if (matchingStatus?.execution_node_id) {
          setSelectedExecutionNodeId(matchingStatus.execution_node_id);
          // Auto-expand the parent loop if this is a loop body node
          if (fullExecutionData.execution_tree.children) {
            for (const child of fullExecutionData.execution_tree.children) {
              const hasRef = child.node_refs?.some(
                (ref: string) => ref === matchingStatus.execution_node_id,
              );
              if (hasRef && child.loop_node_name) {
                const parentExecId =
                  fullExecutionData.execution_tree.node_refs?.find(
                    (ref: string) => ref.endsWith(`__${child.loop_node_name}`),
                  );
                if (parentExecId) {
                  setExpandedLoops((prev) => ({
                    ...prev,
                    [parentExecId]: true,
                  }));
                }
                break;
              }
            }
          }
        }
      } else {
        setSelectedExecutionNodeId(null);
      }

      // Auto-switch to Error tab if the node failed
      if (node.data.executionStatus === "error") {
        setActiveTab("error");
      } else {
        // Check for top-level error matching this node
        const execution = executions.find(
          (ex) => ex._id === selectedExecutionId,
        );
        const errorObj =
          execution?.error || execution?.overall_proccessing?.error;
        if (
          errorObj &&
          (errorObj.node_name === node.data.name ||
            errorObj.node_name === node.id)
        ) {
          setActiveTab("error");
        } else {
          setActiveTab("outputs");
        }
      }
    },
    [executions, selectedExecutionId, fullExecutionData],
  );

  const onPaneClick = useCallback(() => {
    setSelectedNode(null);
    setSelectedStepId(null);
  }, []);

  // Helper to derive execution steps from node_status
  const getExecutionSteps = useCallback(() => {
    if (!selectedExecutionId || !fullExecutionData) return [];
    const execution = fullExecutionData;

    if (execution.node_status && execution.node_status.length > 0) {
      // Build a lookup from execution.nodes (unique only) as a reliable fallback
      // ReactFlow `nodes` state may not be populated yet due to async setNodes
      const execNodeMap = new Map<string, any>();
      if (execution.nodes && Array.isArray(execution.nodes)) {
        for (const n of execution.nodes) {
          if (!execNodeMap.has(n.id)) {
            execNodeMap.set(n.id, n);
          }
        }
      }
      const execNodeList = Array.from(execNodeMap.values());

      // Track which nodes have been matched to avoid duplicate assignments
      const matchedNodeIds = new Set<string>();
      // Track occurrence count per node name for matching correct iteration in nodes[]
      const nodeNameOccurrences: Record<string, number> = {};

      return execution.node_status.map((status: any, index: number) => {
        const isLoopEntry =
          status.for_loop_id !== undefined ||
          status.iteration_index !== undefined;

        // Find node definition from ReactFlow nodes first, then fallback to execution.nodes
        const searchNodes = nodes.length > 0 ? nodes : [];

        // Strategy 1: Exact match by id or name
        let nodeDef: any = searchNodes.find(
          (n) =>
            (isLoopEntry || !matchedNodeIds.has(n.id)) &&
            (n.id === status.node_name || n.data.name === status.node_name),
        );

        // Strategy 2: Match by name prefix (e.g., "HttpAPINode" matches "HttpAPINode1")
        if (!nodeDef) {
          nodeDef = searchNodes.find(
            (n) =>
              (isLoopEntry || !matchedNodeIds.has(n.id)) &&
              n.data.name?.startsWith(status.node_name),
          );
        }

        // Strategy 3: Fallback to execution.nodes (always available, not affected by React state timing)
        if (!nodeDef) {
          // Exact name match first, then prefix match (status.node_name is prefix of n.name)
          const execNode =
            execNodeList.find((n: any) => n.name === status.node_name) ||
            execNodeList.find(
              (n: any) =>
                n.name?.startsWith(status.node_name) &&
                n.name !== status.node_name,
            );
          if (execNode) {
            // Wrap as a pseudo-nodeDef matching ReactFlow node shape
            nodeDef = {
              id: execNode.id,
              type: execNode.type,
              data: {
                name: execNode.name,
                index: execNode.index,
                ...execNode.data,
              },
            };
          }
        }

        // Strategy 4: Fallback to matching by execution order/index (non-loop only)
        if (!nodeDef && !isLoopEntry) {
          nodeDef = searchNodes.find(
            (n) => !matchedNodeIds.has(n.id) && n.data.index === index + 1,
          );
        }

        // Mark this node as matched (only for non-loop entries)
        if (nodeDef && !isLoopEntry) {
          matchedNodeIds.add(nodeDef.id);
        }

        // Track occurrence for matching correct nodes[] entry per iteration
        const resolvedName = nodeDef?.data?.name || status.node_name;
        if (!nodeNameOccurrences[resolvedName]) {
          nodeNameOccurrences[resolvedName] = 0;
        }
        const occurrenceIndex = nodeNameOccurrences[resolvedName];
        nodeNameOccurrences[resolvedName]++;

        let historicalData: any = {};

        if (status.inputs || status.outputs || status.data) {
          historicalData = {
            inputs: status.inputs || status.data?.inputs || {},
            outputs: status.outputs || status.data?.outputs || {},
          };
        }

        // AUTO-INJECT STATUS LEVEL ERROR (from node_status array item)
        if (status.status === "FAILED" || status.status === "ERROR") {
          if (status.error) historicalData.error = status.error;
          if (status.error_description)
            historicalData.error_description = status.error_description;
          if (status.error_details)
            historicalData.error_details = status.error_details;

          // If error_details has description but top level doesn't
          if (
            !historicalData.error_description &&
            status.error_details?.error_description
          ) {
            historicalData.error_description =
              status.error_details.error_description;
          }
        }

        // STRATEGY 2: Match by node name in execution.nodes — use occurrence index for correct iteration
        else if (execution.nodes && Array.isArray(execution.nodes)) {
          const matchingNodes = execution.nodes.filter(
            (n: any) =>
              n.name === resolvedName ||
              n.name === status.node_name ||
              n.id === (nodeDef?.id || status.node_name) ||
              n.name?.startsWith(status.node_name),
          );
          const matchedNode =
            matchingNodes[occurrenceIndex] || matchingNodes[0] || null;

          if (matchedNode) {
            historicalData = { ...matchedNode.data, ...historicalData };
          }
        }

        // STRATEGY 3: Fallback to static node definition (Might only show final state)
        if (!historicalData.inputs && !historicalData.outputs) {
          historicalData = { ...nodeDef?.data, ...historicalData };
        }

        // AUTO-INJECT EXECUTION LEVEL ERROR (Top Level Override/Fallback)
        if (
          execution.node_name === status.node_name &&
          (execution.error || execution.error_description)
        ) {
          if (!historicalData.error)
            historicalData.error =
              execution.error ||
              execution.error_description ||
              "Node execution failed";
          if (!historicalData.error_description)
            historicalData.error_description = execution.error_description;
          if (!historicalData.error_details)
            historicalData.error_details = execution.error_details;
          historicalData.status = "FAILED";
        } else if (status.status === "FAILED" && !historicalData.error) {
          if (
            execution.error_description &&
            execution.node_name === status.node_name
          ) {
            historicalData.error_description = execution.error_description;
          }
          if (
            execution.error_details &&
            execution.node_name === status.node_name
          ) {
            historicalData.error_details = execution.error_details;
          }
        }

        return {
          id: status.id || `${status.node_name}-${index}`,
          nodeId: nodeDef?.id || status.node_name,
          name: nodeDef?.data?.name || status.node_name,
          type: nodeDef?.type || "unknown",
          status: status.status,
          executionStatus:
            status.status === "DONE"
              ? "success"
              : status.status === "FAILED" || status.status === "ERROR"
                ? "error"
                : "running",
          startTime: status.start_time,
          endTime: status.end_time,
          timeTaken: status.time_taken,
          historicalData,
          // Loop metadata
          for_loop_id: status.for_loop_id,
          iteration_index: status.iteration_index,
          iteration_total: status.iteration_total,
          is_loop_complete: status.is_loop_complete,
        };
      });
    }

    // Fallback if no node_status (e.g. legacy or empty)
    return nodes.map((node) => ({
      id: node.id,
      nodeId: node.id,
      name: node.data.name || node.id,
      type: node.type,
      status: node.data.executionStatus,
      executionStatus: node.data.executionStatus,
      startTime: null,
      endTime: null,
      timeTaken: null,
      historicalData: node.data,
    }));
  }, [selectedExecutionId, fullExecutionData, nodes]);

  const executionSteps = getExecutionSteps();

  // Group steps by Node ID
  const groupedSteps = useMemo(() => {
    const groups: Record<string, any[]> = {};
    executionSteps.forEach((step: any) => {
      if (!groups[step.nodeId]) {
        groups[step.nodeId] = [];
      }
      groups[step.nodeId].push(step);
    });

    // Convert to array preserving the order of *first appearance*
    // or just map generic nodes and attach steps?
    // Let's use the executionSteps order to determine the list order,
    // but filter out subsequent appearances of the same node
    const uniqueIds = new Set();
    const result: any[] = [];

    executionSteps.forEach((step: any) => {
      if (!uniqueIds.has(step.nodeId)) {
        uniqueIds.add(step.nodeId);
        result.push({
          ...step,
          iterations: groups[step.nodeId],
        });
      }
    });

    return result;
  }, [executionSteps]);

  // Build tree-structured steps from execution_tree / execution_node_id patterns
  const treeSteps = useMemo(() => {
    const tree = fullExecutionData?.execution_tree;
    const nodeStatuses = fullExecutionData?.node_status as any[] | undefined;
    const execNodes = fullExecutionData?.nodes as any[] | undefined;

    // Guard: need execution_tree to be present
    if (!tree) {
      return null;
    }

    // Build lookup maps by execution_node_id
    const statusMap = new Map<string, any>();
    const dataMap = new Map<string, any>();

    if (Array.isArray(nodeStatuses)) {
      nodeStatuses.forEach((s: any) => {
        if (s.execution_node_id) statusMap.set(s.execution_node_id, s);
      });
    }
    if (Array.isArray(execNodes)) {
      execNodes.forEach((n: any) => {
        if (n.execution_node_id) dataMap.set(n.execution_node_id, n);
      });
    }

    const buildStep = (execNodeId: string) => {
      const status = statusMap.get(execNodeId);
      const nodeData = dataMap.get(execNodeId);
      const execStatus =
        status?.status === "DONE"
          ? "success"
          : status?.status === "FAILED" || status?.status === "ERROR"
            ? "error"
            : status?.status
              ? "running"
              : undefined;

      return {
        execution_node_id: execNodeId,
        name:
          nodeData?.name ||
          status?.node_name ||
          execNodeId.split("__").pop() ||
          "",
        type: nodeData?.type || "unknown",
        nodeId: nodeData?.id || "",
        status: status?.status || "PENDING",
        executionStatus: execStatus,
        startTime: status?.start_time,
        endTime: status?.end_time,
        timeTaken: status?.time_taken,
        historicalData: nodeData?.data || {},
        error: status?.error,
        error_description: status?.error_description,
        error_details: status?.error_details,
      };
    };

    // ---------- Strategy 1: Use execution_tree if it's complete ----------
    const treeNodeRefs = Array.isArray(tree.node_refs) ? tree.node_refs : [];
    const treeChildren = Array.isArray(tree.children) ? tree.children : [];

    // Count how many root-level execution_node_ids exist (pattern: "root__NodeName")
    const allExecNodeIds = Array.from(
      new Set([...Array.from(statusMap.keys()), ...Array.from(dataMap.keys())]),
    );
    const rootExecIds = allExecNodeIds.filter((id) => {
      const lastDunder = id.lastIndexOf("__");
      if (lastDunder === -1) return true;
      return id.substring(0, lastDunder) === "root";
    });
    const isTreeComplete = treeNodeRefs.length >= rootExecIds.length;

    console.log(
      "[treeSteps] isTreeComplete:",
      isTreeComplete,
      "treeNodeRefs:",
      treeNodeRefs.length,
      "rootExecIds:",
      rootExecIds.length,
    );
    console.log(
      "[treeSteps] treeChildren:",
      treeChildren.length,
      treeChildren.map((c: any) => c.scope_id),
    );

    if (isTreeComplete && treeNodeRefs.length > 0) {
      const includedIds = new Set<string>();

      // Recursive helper: builds steps for a list of node_refs within a scope's children
      const buildScopeSteps = (refs: string[], scopeChildren: any[]): any[] => {
        return refs.map((ref: string) => {
          includedIds.add(ref);
          const nd = dataMap.get(ref);
          console.log(
            "[buildScopeSteps] ref:",
            ref,
            "type:",
            nd?.type,
            "scopeChildren:",
            scopeChildren.map((c: any) => c.scope_id),
          );
          if (nd?.type === "FOR-EACH") {
            const childScopes = scopeChildren.filter(
              (c: any) => c.loop_node_name === nd.name,
            );
            console.log(
              "[buildScopeSteps] FOR-EACH:",
              nd.name,
              "childScopes:",
              childScopes.length,
            );
            const iters = childScopes.map((cs: any) => ({
              scope_id: cs.scope_id,
              index: cs.iteration_index,
              totalItems: cs.total_items,
              // Recurse into this scope's own children for deeper nesting
              steps: buildScopeSteps(cs.node_refs || [], cs.children || []),
              children: cs.children || [],
            }));
            return {
              ...buildStep(ref),
              isLoop: true,
              totalItems: iters[0]?.totalItems || childScopes.length,
              iterations: iters,
            };
          }
          return buildStep(ref);
        });
      };

      const steps = buildScopeSteps(treeNodeRefs, treeChildren);
      console.log(
        "[treeSteps] Strategy1 steps:",
        steps.map((s: any) => ({
          name: s.name,
          isLoop: s.isLoop,
          iters: s.iterations?.length,
        })),
      );

      // Append any missed entries
      for (const s of nodeStatuses || []) {
        if (s.execution_node_id && !includedIds.has(s.execution_node_id)) {
          includedIds.add(s.execution_node_id);
          steps.push(buildStep(s.execution_node_id));
        }
      }

      if (steps.length > 0) return steps;
    }

    // ---------- Strategy 2: Build tree from execution_node_id patterns ----------
    // When execution_tree is incomplete (e.g. node_refs has only 1 entry, children is empty),
    // parse the execution_node_id to infer the hierarchy:
    //   "root__NodeName"                       → root-level node
    //   "root/LoopName[index]__NodeName"       → child of LoopName at iteration index
    const rootSteps: any[] = [];
    const loopChildMap = new Map<string, Map<number, any[]>>();
    const seenIds = new Set<string>();

    // Collect all execution_node_ids in order (node_status order is execution order)
    const allIds: string[] = [];
    if (Array.isArray(nodeStatuses)) {
      nodeStatuses.forEach((s: any) => {
        if (s.execution_node_id && !seenIds.has(s.execution_node_id)) {
          seenIds.add(s.execution_node_id);
          allIds.push(s.execution_node_id);
        }
      });
    }
    if (Array.isArray(execNodes)) {
      execNodes.forEach((n: any) => {
        if (n.execution_node_id && !seenIds.has(n.execution_node_id)) {
          seenIds.add(n.execution_node_id);
          allIds.push(n.execution_node_id);
        }
      });
    }

    // Pattern: scope/LoopName[index]__NodeName
    const loopIterationRegex = /^(.+?)\/([^/[]+)\[(\d+)\]__(.+)$/;

    for (const execId of allIds) {
      const match = execId.match(loopIterationRegex);
      if (match) {
        const loopNodeName = match[2]; // e.g. "ForEachNode1"
        const iterIndex = parseInt(match[3], 10);
        if (!loopChildMap.has(loopNodeName)) {
          loopChildMap.set(loopNodeName, new Map());
        }
        const iterMap = loopChildMap.get(loopNodeName)!;
        if (!iterMap.has(iterIndex)) {
          iterMap.set(iterIndex, []);
        }
        iterMap.get(iterIndex)!.push(buildStep(execId));
      } else {
        rootSteps.push(buildStep(execId));
      }
    }

    // Assemble final steps: attach loop iterations under their parent node
    const finalSteps: any[] = [];
    for (const step of rootSteps) {
      const nodeData = dataMap.get(step.execution_node_id);
      const nodeName = nodeData?.name || step.name;
      const childIterations = loopChildMap.get(nodeName);

      if (childIterations && childIterations.size > 0) {
        const sortedIndices = Array.from(childIterations.keys()).sort(
          (a, b) => a - b,
        );
        const iterations = sortedIndices.map((idx) => ({
          scope_id: `${step.execution_node_id}[${idx}]`,
          index: idx,
          totalItems: sortedIndices.length,
          steps: childIterations.get(idx)!,
          children: [],
        }));

        finalSteps.push({
          ...step,
          isLoop: true,
          totalItems: sortedIndices.length,
          iterations,
        });
      } else {
        finalSteps.push(step);
      }
    }

    return finalSteps.length > 0 ? finalSteps : null;
  }, [fullExecutionData]);

  // Derive the currently selected step based on Node + Iteration
  const activeStep = useMemo(() => {
    // Tree mode: direct lookup by execution_node_id
    if (selectedExecutionNodeId && fullExecutionData) {
      const status = fullExecutionData.node_status?.find(
        (s: any) => s.execution_node_id === selectedExecutionNodeId,
      );
      const nodeData = fullExecutionData.nodes?.find(
        (n: any) => n.execution_node_id === selectedExecutionNodeId,
      );

      if (status || nodeData) {
        const execStatus =
          status?.status === "DONE"
            ? "success"
            : status?.status === "FAILED" || status?.status === "ERROR"
              ? "error"
              : status?.status
                ? "running"
                : undefined;

        return {
          id: status?.id || nodeData?.id || selectedExecutionNodeId,
          execution_node_id: selectedExecutionNodeId,
          nodeId: nodeData?.id || status?.node_name,
          name: nodeData?.name || status?.node_name,
          type: nodeData?.type || "unknown",
          status: status?.status || "PENDING",
          executionStatus: execStatus,
          startTime: status?.start_time,
          endTime: status?.end_time,
          timeTaken: status?.time_taken,
          historicalData: {
            ...(nodeData?.data || {}),
            error: status?.error,
            error_description: status?.error_description,
            error_details: status?.error_details,
          },
        };
      }
    }

    // Fallback: legacy flat mode
    if (!selectedNode) return null;
    const group = groupedSteps.find((g) => g.nodeId === selectedNode.id);
    if (!group) return null;

    // Safety check for index
    const index = Math.min(
      Math.max(0, selectedIterationIndex),
      group.iterations.length - 1,
    );
    return group.iterations[index];
  }, [
    selectedNode,
    groupedSteps,
    selectedIterationIndex,
    selectedExecutionNodeId,
    fullExecutionData,
  ]);

  // NEW Helper to get data for tabs
  const getActiveNodeData = () => {
    if (!activeStep) return selectedNode?.data || {};

    // Extract error info from various possible locations in historical data
    const historicalData = activeStep.historicalData || {};
    const errorDescription =
      historicalData.error_description ||
      historicalData.error_details?.error_description;

    return {
      ...selectedNode?.data,
      ...historicalData, // Merge all historical data properties
      executionStatus: activeStep.executionStatus,
      startTime: activeStep.startTime,
      endTime: activeStep.endTime,
      timeTaken: activeStep.timeTaken,
      // Propagate error from step
      error:
        activeStep.status === "FAILED" || activeStep.status === "ERROR"
          ? historicalData.error || selectedNode?.data?.error || "Step failed"
          : undefined,
      error_description: errorDescription,
      error_details: historicalData.error_details,
    };
  };

  // Resize Handler Logic
  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return;

      const newHeight = window.innerHeight - e.clientY;
      // Min height 200px, Max height 80% of window
      const constrainedHeight = Math.min(
        Math.max(200, newHeight),
        window.innerHeight * 0.8,
      );
      setPanelHeight(constrainedHeight);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isResizing]);

  const isHistoryMode = !!templateId || true; // Force true for mock data

  if (!isHistoryMode) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-gray-400 bg-gray-50">
        <p>Please save the workflow to view execution history.</p>
      </div>
    );
  }

  // Recursive step renderer — handles any nesting depth
  const renderStep = (
    step: any,
    numPrefix: string,
    depth: number,
  ): React.ReactNode => {
    const isSelected = selectedExecutionNodeId === step.execution_node_id;

    if (step.isLoop) {
      const isExpanded = !!expandedLoops[step.execution_node_id];
      const indentBorders = [
        "border-violet-200",
        "border-indigo-200",
        "border-teal-200",
        "border-purple-200",
      ];
      const borderColor = indentBorders[depth % indentBorders.length];

      // Collect direct children across all iterations with sequential numbering
      const children: { childStep: any; num: string }[] = [];
      let counter = 0;
      (step.iterations || []).forEach((iter: any) => {
        (iter.steps || []).forEach((s: any) => {
          counter++;
          children.push({ childStep: s, num: `${numPrefix}.${counter}` });
        });
      });

      return (
        <div key={step.execution_node_id}>
          <div
            onClick={(e) => {
              e.stopPropagation();
              setExpandedLoops((prev) => ({
                ...prev,
                [step.execution_node_id]: !prev[step.execution_node_id],
              }));
              const nodeToSelect = nodes.find((n) => n.id === step.nodeId);
              if (nodeToSelect) setSelectedNode({ ...nodeToSelect });
              setSelectedExecutionNodeId(step.execution_node_id);
              setIsPanelExpanded(true);
              setActiveTab("outputs");
            }}
            className={`rounded-md cursor-pointer transition-all duration-150 flex items-center ${
              depth === 0 ? "p-2 gap-2.5" : "p-1.5 pl-3 gap-2"
            } ${isSelected ? "bg-blue-50 border border-blue-200 shadow-sm" : "hover:bg-gray-50 border border-transparent"}`}
          >
            {depth > 0 && (
              <span className="text-[10px] font-bold text-violet-500 w-6 flex-shrink-0">
                {numPrefix}
              </span>
            )}
            {getStatusIcon(step.executionStatus)}
            <div className="overflow-hidden flex-1">
              <div className="flex justify-between items-center">
                <p
                  className={`font-medium truncate ${depth === 0 ? "text-sm" : "text-xs"} ${isSelected ? "text-blue-700" : "text-gray-800"}`}
                >
                  {step.name}
                </p>
                <div className="flex items-center gap-1 flex-shrink-0">
                  {step.totalItems > 1 && (
                    <span className="text-[9px] bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded-full font-semibold border border-blue-200 flex items-center gap-0.5">
                      {step.totalItems} runs
                    </span>
                  )}
                  <ChevronDown
                    className={`w-3.5 h-3.5 text-gray-400 transition-transform ${isExpanded ? "" : "-rotate-90"}`}
                  />
                </div>
              </div>
              <p className="text-[10px] text-gray-400 lowercase">for each</p>
            </div>
          </div>
          {isExpanded && (
            <div className={`ml-4 border-l-2 ${borderColor} mt-0.5 mb-1`}>
              {children.map(({ childStep, num }) =>
                renderStep(childStep, num, depth + 1),
              )}
            </div>
          )}
        </div>
      );
    }

    // Regular (non-loop) node
    return (
      <div
        key={step.execution_node_id}
        onClick={(e) => {
          e.stopPropagation();
          const nodeToSelect = nodes.find((n) => n.id === step.nodeId);
          if (nodeToSelect) setSelectedNode({ ...nodeToSelect });
          setSelectedExecutionNodeId(step.execution_node_id);
          setIsPanelExpanded(true);
          setActiveTab(step.executionStatus === "error" ? "error" : "outputs");
        }}
        className={`rounded-md cursor-pointer transition-all duration-150 flex items-center ${
          depth === 0 ? "p-2 gap-2.5" : "p-1.5 pl-3 gap-2"
        } ${isSelected ? "bg-blue-50 border border-blue-200 shadow-sm" : "hover:bg-gray-50 border border-transparent"}`}
      >
        {depth > 0 && (
          <span className="text-[10px] font-bold text-violet-500 w-6 flex-shrink-0">
            {numPrefix}
          </span>
        )}
        {getStatusIcon(step.executionStatus)}
        <div className="overflow-hidden flex-1">
          <div className="flex justify-between items-center">
            <p
              className={`font-medium truncate ${depth === 0 ? "text-sm" : "text-xs"} ${isSelected ? "text-blue-700" : "text-gray-800"}`}
            >
              {step.name}
            </p>
            {step.iterations?.length > 1 && (
              <span className="text-[9px] bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded-full font-semibold border border-blue-200">
                {step.iterations.length} runs
              </span>
            )}
          </div>
          <p className="text-[10px] text-gray-400 truncate lowercase">
            {step.type !== "unknown" ? step.type.replace("-", " ") : ""}
          </p>
        </div>
      </div>
    );
  };

  return (
    <div className="flex h-[91vh] bg-white border-t border-gray-200 overflow-hidden">
      {/* Left Sidebar: Executions List */}
      <div
        className="w-80 border-r border-gray-200 flex flex-col bg-white overflow-hidden"
        style={{ height: "100%" }} // FIX: sidebar fixed full height
      >
        <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2.5">
            <Zap className="w-4.5 h-4.5 text-blue-600" />
            Executions
          </h2>
          <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
            {executions.length} {executions.length === 1 ? "run" : "runs"}
          </span>
        </div>

        <div className="flex-1 overflow-y-auto bg-gray-50/50 custom-scrollbar">
          {isLoading ? (
            <div className="flex justify-center items-center h-32">
              <Loader className="w-5 h-5 text-blue-600 animate-spin" />
            </div>
          ) : executions.length === 0 ? (
            <div className="p-12 text-center">
              <Layout className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-sm text-gray-500">No executions yet</p>
              <p className="text-xs text-gray-400 mt-1">
                Run a workflow to see executions here
              </p>
            </div>
          ) : (
            <div className="p-3">
              <ExecutionTimeline
                executions={executions}
                selectedExecutionId={selectedExecutionId}
                onSelectExecution={setSelectedExecutionId}
              />
            </div>
          )}
        </div>

        {/* Pagination Controls */}
        {!isLoading && executions.length > 0 && (
          <div className="border-t border-gray-200 bg-white px-4 py-3 flex items-center justify-between">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(0, prev - 1))}
              disabled={currentPage === 0}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                currentPage === 0
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-blue-50 text-blue-700 hover:bg-blue-100"
              }`}
            >
              Previous
            </button>

            <div className="text-xs text-gray-600">
              <span className="font-medium">Page {currentPage + 1}</span>
              {totalCount > 0 && (
                <span className="text-gray-400 ml-1">
                  ({currentPage * LIMIT + 1}-
                  {Math.min((currentPage + 1) * LIMIT, totalCount)} of{" "}
                  {totalCount})
                </span>
              )}
            </div>

            <button
              onClick={() => setCurrentPage((prev) => prev + 1)}
              disabled={executions.length < LIMIT}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                executions.length < LIMIT
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-blue-50 text-blue-700 hover:bg-blue-100"
              }`}
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* Right Main Area: Canvas */}
      <div className="flex-1 relative bg-gray-50 flex flex-col">
        {selectedExecutionId ? (
          <div className="flex-1 flex flex-col relative overflow-hidden">
            {/* Canvas Header */}
            <div className="h-12 bg-white border-b border-gray-200 flex items-center px-4 justify-between shrink-0">
              <div className="flex items-center gap-3">
                <span className="font-semibold text-gray-700">
                  Execution View
                </span>
                {fullExecutionData && (
                  <div className="flex items-center gap-2">
                    <span
                      className={`text-[10px] px-2 py-0.5 rounded-full font-bold border ${
                        fullExecutionData.overall_proccessing_status === "DONE"
                          ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                          : fullExecutionData.overall_proccessing_status ===
                              "FAILED"
                            ? "bg-rose-50 text-rose-700 border-rose-200"
                            : "bg-blue-50 text-blue-600 border-blue-200"
                      }`}
                    >
                      {fullExecutionData.overall_proccessing_status}
                    </span>
                    {fullExecutionData.overall_proccessing?.time_taken !=
                      null && (
                      <span className="text-[10px] text-gray-500 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {fullExecutionData.overall_proccessing.time_taken.toFixed(
                          1,
                        )}
                        s
                      </span>
                    )}
                    <span className="text-[10px] text-gray-400">
                      {fullExecutionData.completed_steps ?? 0}/
                      {fullExecutionData.total_steps ?? 0} steps
                    </span>
                    {fullExecutionData.total_token_usage > 0 && (
                      <span className="text-[10px] text-blue-600 flex items-center gap-1">
                        <Cpu className="w-3 h-3" />
                        {formatTokenCount(fullExecutionData.total_token_usage)}
                      </span>
                    )}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-3">
                <div className="text-xs text-gray-400">Read-only mode</div>
                {fullExecutionData && (
                  <button
                    onClick={() => setShowTokenAnalytics(true)}
                    className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 transition-colors"
                  >
                    <Cpu className="w-3.5 h-3.5" />
                    Token Analytics
                  </button>
                )}
              </div>
            </div>

            {/* ReactFlow Canvas */}
            <div className="flex-1 relative">
              {isLoadingExecution && nodes.length === 0 && (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-50/80 z-10">
                  <Loader className="w-6 h-6 text-blue-500 animate-spin" />
                </div>
              )}
              <ReactFlowProvider key={`rf-${selectedExecutionId}`}>
                <ReactFlow
                  nodes={nodes}
                  edges={edges}
                  onNodesChange={onNodesChange}
                  onEdgesChange={onEdgesChange}
                  nodeTypes={nodeTypes}
                  edgeTypes={edgeTypes}
                  onNodeClick={onNodeClick}
                  onPaneClick={onPaneClick}
                  fitView
                  attributionPosition="bottom-right"
                  minZoom={0.1}
                  maxZoom={2}
                  nodesDraggable={false}
                  nodesConnectable={false}
                  elementsSelectable={true}
                >
                  <Background
                    id="1"
                    gap={10}
                    color="#969595ff"
                    variant={BackgroundVariant.Dots}
                  />
                  <Controls
                    orientation="horizontal"
                    position="top-right"
                    className="bg-white shadow-lg rounded-lg border border-gray-200"
                  />
                  <MiniMap
                    className="bg-white shadow-lg rounded-lg border border-gray-200"
                    nodeColor="#e5e7eb"
                    maskColor="rgba(0,0,0,0.1)"
                  />
                </ReactFlow>
              </ReactFlowProvider>
            </div>

            {/* Optimized Bottom Panel for Node Details */}
            <div
              className={`absolute bottom-0 left-0 right-0 bg-white border-t border-gray-300 shadow-[0_-2px_10px_rgba(0,0,0,0.08)] transition-all duration-300 ease-in-out z-50 flex flex-col`}
              style={{
                height: isPanelExpanded ? `${panelHeight}px` : "44px",
                transition: isResizing ? "none" : "height 300ms ease-in-out",
              }}
            >
              {/* Resize Handle */}
              <div
                className="absolute top-0 left-0 right-0 h-4 -mt-2 cursor-row-resize z-50 flex items-center justify-center group"
                onMouseDown={(e) => {
                  handleMouseDown(e);
                  if (!isPanelExpanded) setIsPanelExpanded(true);
                }}
              >
                <div className="w-16 h-1 bg-gray-200 rounded-full group-hover:bg-blue-500 transition-colors shadow-sm border border-gray-300 group-hover:border-blue-600" />
              </div>

              {/* Panel Header */}
              <div
                className="h-11 flex items-center justify-between px-5 bg-white border-b border-gray-200 cursor-pointer hover:bg-gray-50 transition-colors shrink-0"
                onClick={() => setIsPanelExpanded(!isPanelExpanded)}
              >
                <div className="flex items-center gap-3">
                  <button className="text-gray-400 hover:text-gray-600 transition-colors">
                    {isPanelExpanded ? (
                      <ChevronDown className="w-4 h-4" />
                    ) : (
                      <ChevronUp className="w-4 h-4" />
                    )}
                  </button>
                  {selectedNode ? (
                    <>
                      {getStatusIcon(selectedNode.data.executionStatus)}
                      <span className="font-semibold text-gray-900 text-sm">
                        {selectedNode.data.name || selectedNode.id}
                      </span>
                      <span
                        className={`text-[10px] px-2 py-0.5 rounded-full font-bold border ml-2 ${
                          selectedNode.data.executionStatus === "success"
                            ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                            : selectedNode.data.executionStatus === "error"
                              ? "bg-rose-50 text-rose-700 border-rose-200"
                              : "bg-gray-100 text-gray-600 border-gray-200"
                        }`}
                      >
                        {selectedNode.data.executionStatus || "PENDING"}
                      </span>
                    </>
                  ) : (
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-gray-900 text-sm">
                        Execution Details
                      </span>
                      {fullExecutionData?.overall_proccessing_status && (
                        <span
                          className={`text-[10px] px-2 py-0.5 rounded-full font-bold border ${
                            fullExecutionData.overall_proccessing_status ===
                            "DONE"
                              ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                              : fullExecutionData.overall_proccessing_status ===
                                  "FAILED"
                                ? "bg-rose-50 text-rose-700 border-rose-200"
                                : "bg-blue-50 text-blue-600 border-blue-200"
                          }`}
                        >
                          {fullExecutionData.overall_proccessing_status}
                        </span>
                      )}
                      {fullExecutionData?.overall_proccessing?.time_taken !=
                        null && (
                        <span className="text-[10px] text-gray-500 bg-gray-50 px-2 py-0.5 rounded-full border border-gray-200 flex items-center gap-1">
                          <Clock className="w-2.5 h-2.5" />
                          {fullExecutionData.overall_proccessing.time_taken.toFixed(
                            1,
                          )}
                          s
                        </span>
                      )}
                      {fullExecutionData?.total_token_usage != null &&
                        fullExecutionData.total_token_usage > 0 && (
                          <span className="text-[10px] text-blue-700 bg-blue-50 px-2 py-0.5 rounded-full border border-blue-100 flex items-center gap-1">
                            <Cpu className="w-2.5 h-2.5" />
                            {formatTokenCount(
                              fullExecutionData.total_token_usage,
                            )}{" "}
                            tokens
                          </span>
                        )}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsPanelExpanded(false);
                    }}
                    className="p-1.5 hover:bg-gray-100 rounded-md text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <XCircle className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Panel Content Body */}
              <div className="flex-1 flex overflow-hidden">
                {/* Left Column: Steps List */}
                <div className="w-72 border-r border-gray-200 flex flex-col bg-gray-50/50 shrink-0">
                  <div className="h-10 bg-white border-b border-gray-200 flex items-center px-4">
                    <span className="text-xs font-bold text-gray-500 uppercase tracking-wide">
                      Workflow Steps
                    </span>
                  </div>
                  <div className="flex-1 overflow-y-auto p-2">
                    {isLoadingExecution ? (
                      <div className="flex justify-center items-center h-24">
                        <Loader className="w-4 h-4 text-blue-500 animate-spin" />
                        <span className="text-xs text-gray-400 ml-2">
                          Loading steps...
                        </span>
                      </div>
                    ) : treeSteps && treeSteps.length > 0 ? (
                      /* ─── Tree-based rendering (execution_tree available) ─── */
                      <div className="space-y-0.5">
                        {treeSteps.map((step: any, stepIdx: number) =>
                          renderStep(step, String(stepIdx + 1), 0),
                        )}
                      </div>
                    ) : groupedSteps.length === 0 ? (
                      <div className="flex flex-col items-center justify-center h-24 text-gray-400">
                        <p className="text-xs">No step data available</p>
                      </div>
                    ) : (
                      /* ─── Fallback: flat rendering with sub-numbering for loops ─── */
                      <div className="space-y-0.5">
                        {groupedSteps.map((group: any) => {
                          const statusObj =
                            group.iterations[group.iterations.length - 1];
                          const isSelected =
                            selectedNode?.id === group.nodeId &&
                            !selectedExecutionNodeId;

                          return (
                            <div key={group.nodeId}>
                              <div
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const nodeToSelect = nodes.find(
                                    (n) => n.id === group.nodeId,
                                  );
                                  if (nodeToSelect) {
                                    setSelectedNode({ ...nodeToSelect });
                                    setSelectedIterationIndex(0);
                                    setSelectedExecutionNodeId(null);
                                    setIsPanelExpanded(true);
                                  }
                                  if (statusObj.executionStatus === "error")
                                    setActiveTab("error");
                                  else setActiveTab("outputs");
                                }}
                                className={`p-2 rounded-md cursor-pointer transition-all duration-150 flex items-center gap-2.5 ${
                                  isSelected
                                    ? "bg-blue-50 border border-blue-200 shadow-sm"
                                    : "hover:bg-gray-50 border border-transparent"
                                }`}
                              >
                                {getStatusIcon(statusObj.executionStatus)}
                                <div className="overflow-hidden flex-1 text-left">
                                  <div className="flex justify-between items-center">
                                    <p
                                      className={`text-sm font-medium truncate ${isSelected ? "text-blue-700" : "text-gray-800"}`}
                                    >
                                      {group.name}
                                    </p>
                                    {group.iterations.length > 1 && (
                                      <span className="text-[10px] bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded-full font-semibold border border-blue-100 flex-shrink-0">
                                        {group.iterations.length} runs
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-[10px] text-gray-400 truncate lowercase">
                                    {group.type !== "unknown"
                                      ? group.type.replace("-", " ")
                                      : group.for_loop_id
                                        ? "loop body"
                                        : ""}
                                  </p>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>

                {/* Right Column: Details */}
                <div className="flex-1 flex flex-col bg-white overflow-hidden">
                  {!selectedNode ? (
                    <div className="flex-1 overflow-y-auto p-4">
                      {fullExecutionData ? (
                        <div className="space-y-4">
                          {/* Execution Overview Header */}
                          <div className="flex items-center gap-2 mb-2">
                            <Info className="w-4 w-4 text-blue-500" />
                            <h3 className="text-sm font-semibold text-gray-800">
                              Execution Overview
                            </h3>
                          </div>

                          {/* Status & Timing Cards */}
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            <div className="bg-white p-3 rounded-lg border border-gray-200 shadow-sm">
                              <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1.5">
                                Status
                              </span>
                              <div className="flex items-center gap-2">
                                {getStatusIcon(
                                  fullExecutionData.overall_proccessing_status,
                                )}
                                <span
                                  className={`text-sm font-bold ${
                                    fullExecutionData.overall_proccessing_status ===
                                    "DONE"
                                      ? "text-emerald-700"
                                      : fullExecutionData.overall_proccessing_status ===
                                          "FAILED"
                                        ? "text-rose-700"
                                        : "text-blue-600"
                                  }`}
                                >
                                  {fullExecutionData.overall_proccessing_status ||
                                    "UNKNOWN"}
                                </span>
                              </div>
                            </div>

                            <div className="bg-white p-3 rounded-lg border border-gray-200 shadow-sm">
                              <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1.5">
                                Duration
                              </span>
                              <div className="flex items-center gap-2">
                                <Clock className="w-4 h-4 text-gray-400" />
                                <span className="text-sm font-bold text-gray-800">
                                  {fullExecutionData.overall_proccessing
                                    ?.time_taken != null
                                    ? `${fullExecutionData.overall_proccessing.time_taken.toFixed(2)}s`
                                    : "N/A"}
                                </span>
                              </div>
                            </div>

                            <div className="bg-white p-3 rounded-lg border border-gray-200 shadow-sm">
                              <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1.5">
                                Steps
                              </span>
                              <div className="flex items-center gap-2">
                                <Layers className="w-4 h-4 text-gray-400" />
                                <span className="text-sm font-bold text-gray-800">
                                  {fullExecutionData.completed_steps ?? 0} /{" "}
                                  {fullExecutionData.total_steps ?? 0}
                                </span>
                              </div>
                              <div className="mt-1.5 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                <div
                                  className="h-full bg-blue-500 rounded-full transition-all"
                                  style={{
                                    width: `${fullExecutionData.total_steps ? ((fullExecutionData.completed_steps || 0) / fullExecutionData.total_steps) * 100 : 0}%`,
                                  }}
                                />
                              </div>
                            </div>

                            <div className="bg-white p-3 rounded-lg border border-gray-200 shadow-sm">
                              <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1.5">
                                Total Tokens
                              </span>
                              <div className="flex items-center gap-2">
                                <Cpu className="w-4 h-4 text-blue-500" />
                                <span className="text-sm font-bold text-gray-800">
                                  {fullExecutionData.total_token_usage
                                    ? formatTokenCount(
                                        fullExecutionData.total_token_usage,
                                      )
                                    : "0"}
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* Timing Details */}
                          <div className="grid grid-cols-2 gap-3">
                            <div className="bg-white p-3 rounded-lg border border-gray-200 shadow-sm">
                              <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1">
                                Started At
                              </span>
                              <div className="flex items-center gap-2 text-gray-700">
                                <Calendar className="w-3.5 h-3.5 text-gray-400" />
                                <span className="font-mono text-xs">
                                  {fullExecutionData.overall_proccessing
                                    ?.start_time
                                    ? formatDate(
                                        fullExecutionData.overall_proccessing
                                          .start_time,
                                      )
                                    : fullExecutionData.createdAt
                                      ? formatDate(fullExecutionData.createdAt)
                                      : "-"}
                                </span>
                              </div>
                            </div>
                            <div className="bg-white p-3 rounded-lg border border-gray-200 shadow-sm">
                              <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1">
                                Finished At
                              </span>
                              <div className="flex items-center gap-2 text-gray-700">
                                <Calendar className="w-3.5 h-3.5 text-gray-400" />
                                <span className="font-mono text-xs">
                                  {fullExecutionData.overall_proccessing
                                    ?.end_time
                                    ? formatDate(
                                        fullExecutionData.overall_proccessing
                                          .end_time,
                                      )
                                    : "-"}
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* Token Usage per Node */}
                          {fullExecutionData.token_usages &&
                            fullExecutionData.token_usages.length > 0 && (
                              <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
                                <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
                                  <div className="flex items-center gap-2">
                                    <BarChart3 className="w-4 h-4 text-gray-500" />
                                    <span className="text-xs font-bold text-gray-700 uppercase tracking-wide">
                                      Token Usage by Node
                                    </span>
                                  </div>
                                  <span className="text-[10px] text-gray-400 font-medium bg-gray-100 px-2 py-0.5 rounded-full border border-gray-200">
                                    {fullExecutionData.token_usages.length}{" "}
                                    {fullExecutionData.token_usages.length === 1
                                      ? "node"
                                      : "nodes"}
                                  </span>
                                </div>

                                {/* Table Header */}
                                <div className="grid grid-cols-12 gap-2 px-4 py-2 bg-gray-50/80 border-b border-gray-100">
                                  <div className="col-span-3 text-[10px] font-bold text-gray-500 uppercase tracking-wider">
                                    Node
                                  </div>
                                  <div className="col-span-2 text-[10px] font-bold text-gray-500 uppercase tracking-wider">
                                    Model
                                  </div>
                                  <div className="col-span-2 text-[10px] font-bold text-gray-500 uppercase tracking-wider">
                                    Type
                                  </div>
                                  <div className="col-span-2 text-[10px] font-bold text-gray-500 uppercase tracking-wider text-right">
                                    Prompt
                                  </div>
                                  <div className="col-span-1 text-[10px] font-bold text-gray-500 uppercase tracking-wider text-right">
                                    Compl.
                                  </div>
                                  <div className="col-span-2 text-[10px] font-bold text-gray-500 uppercase tracking-wider text-right">
                                    Total
                                  </div>
                                </div>

                                {/* Rows */}
                                <div className="divide-y divide-gray-50">
                                  {fullExecutionData.token_usages.map(
                                    (u: any, idx: number) => (
                                      <div
                                        key={`${u.node_name}-${idx}`}
                                        className="grid grid-cols-12 gap-2 px-4 py-2.5 hover:bg-blue-50/30 transition-colors"
                                      >
                                        <div className="col-span-3 text-xs font-medium text-gray-800 truncate">
                                          {u.node_name}
                                        </div>
                                        <div className="col-span-2 text-[10px] text-gray-500 truncate">
                                          {u.model_name || "-"}
                                        </div>
                                        <div className="col-span-2">
                                          <span className="text-[10px] font-medium text-gray-600 bg-gray-100 px-1.5 py-0.5 rounded">
                                            {u.node_type || "-"}
                                          </span>
                                        </div>
                                        <div className="col-span-2 text-right text-xs text-blue-700 font-mono">
                                          {formatTokenCount(
                                            u.prompt_tokens || 0,
                                          )}
                                        </div>
                                        <div className="col-span-1 text-right text-xs text-teal-700 font-mono">
                                          {formatTokenCount(
                                            u.completion_tokens || 0,
                                          )}
                                        </div>
                                        <div className="col-span-2 text-right text-xs font-bold text-gray-800 font-mono">
                                          {formatTokenCount(
                                            u.total_tokens || 0,
                                          )}
                                        </div>
                                      </div>
                                    ),
                                  )}
                                </div>

                                {/* Totals Row */}
                                <div className="grid grid-cols-12 gap-2 px-4 py-2.5 bg-gray-50 border-t border-gray-200">
                                  <div className="col-span-7 text-xs font-bold text-gray-700 uppercase">
                                    Total
                                  </div>
                                  <div className="col-span-2 text-right text-xs font-bold text-blue-800 font-mono">
                                    {formatTokenCount(
                                      fullExecutionData.token_usages.reduce(
                                        (s: number, u: any) =>
                                          s + (u.prompt_tokens || 0),
                                        0,
                                      ),
                                    )}
                                  </div>
                                  <div className="col-span-1 text-right text-xs font-bold text-teal-800 font-mono">
                                    {formatTokenCount(
                                      fullExecutionData.token_usages.reduce(
                                        (s: number, u: any) =>
                                          s + (u.completion_tokens || 0),
                                        0,
                                      ),
                                    )}
                                  </div>
                                  <div className="col-span-2 text-right text-xs font-bold text-gray-900 font-mono">
                                    {formatTokenCount(
                                      fullExecutionData.token_usages.reduce(
                                        (s: number, u: any) =>
                                          s + (u.total_tokens || 0),
                                        0,
                                      ),
                                    )}
                                  </div>
                                </div>
                              </div>
                            )}

                          {/* Execution ID */}
                          <div className="bg-white p-3 rounded-lg border border-gray-200 shadow-sm">
                            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1">
                              Execution ID
                            </span>
                            <span className="font-mono text-xs text-gray-500 break-all">
                              {fullExecutionData._id ||
                                fullExecutionData.id ||
                                "-"}
                            </span>
                          </div>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center justify-center h-full text-gray-400 bg-gray-50/30">
                          <MousePointerClick className="w-12 h-12 mb-3 opacity-20" />
                          <p className="text-sm font-medium">
                            Select a step to view details
                          </p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <>
                      {/* Node Header with Iteration Selector */}
                      <div className="h-14 border-b border-gray-200 flex items-center justify-between px-6 bg-white shrink-0">
                        <div className="flex items-center gap-3">
                          {getStatusIcon(getActiveNodeData().executionStatus)}
                          <div>
                            <h3 className="text-sm font-bold text-gray-900">
                              {selectedNode?.data?.name || selectedNode?.id}
                            </h3>
                            <p className="text-xs text-gray-500">
                              {selectedNode?.type?.replace("-", " ")}
                            </p>
                          </div>
                        </div>

                        {/* Iteration Selector (hidden in tree mode — iterations are in the sidebar) */}
                        {!treeSteps &&
                          selectedNode &&
                          groupedSteps.find((g) => g.nodeId === selectedNode.id)
                            ?.iterations.length! > 1 && (
                            <div className="flex items-center gap-2 bg-gray-50 rounded-lg p-1 border border-gray-200">
                              <button
                                onClick={() =>
                                  setSelectedIterationIndex((prev) =>
                                    Math.max(0, prev - 1),
                                  )
                                }
                                disabled={selectedIterationIndex === 0}
                                className="p-1 hover:bg-white rounded-md disabled:opacity-30 transition-colors"
                              >
                                <ChevronDown className="w-3 h-3 rotate-90" />
                              </button>
                              <span className="text-xs font-medium text-gray-600 min-w-[80px] text-center">
                                {(() => {
                                  const group = groupedSteps.find(
                                    (g) => g.nodeId === selectedNode.id,
                                  );
                                  const currentIter =
                                    group?.iterations[selectedIterationIndex];
                                  if (currentIter?.is_loop_complete)
                                    return "Loop Complete";
                                  if (currentIter?.iteration_total != null) {
                                    return `Iteration ${(currentIter.iteration_index ?? selectedIterationIndex) + 1} of ${currentIter.iteration_total}`;
                                  }
                                  return `Run ${selectedIterationIndex + 1} / ${group?.iterations.length}`;
                                })()}
                              </span>
                              <button
                                onClick={() =>
                                  setSelectedIterationIndex((prev) =>
                                    Math.min(
                                      (groupedSteps.find(
                                        (g) => g.nodeId === selectedNode.id,
                                      )?.iterations.length || 1) - 1,
                                      prev + 1,
                                    ),
                                  )
                                }
                                disabled={
                                  selectedIterationIndex >=
                                  (groupedSteps.find(
                                    (g) => g.nodeId === selectedNode.id,
                                  )?.iterations.length || 1) -
                                    1
                                }
                                className="p-1 hover:bg-white rounded-md disabled:opacity-30 transition-colors"
                              >
                                <ChevronDown className="w-3 h-3 -rotate-90" />
                              </button>
                              {/* Quick jump to Loop Complete */}
                              {(() => {
                                const group = groupedSteps.find(
                                  (g) => g.nodeId === selectedNode.id,
                                );
                                const loopCompleteIdx =
                                  group?.iterations.findIndex(
                                    (iter: any) => iter.is_loop_complete,
                                  );
                                if (
                                  loopCompleteIdx != null &&
                                  loopCompleteIdx >= 0 &&
                                  selectedIterationIndex !== loopCompleteIdx
                                ) {
                                  return (
                                    <button
                                      onClick={() =>
                                        setSelectedIterationIndex(
                                          loopCompleteIdx,
                                        )
                                      }
                                      className="ml-1 px-2 py-0.5 text-[10px] font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-md hover:bg-emerald-100 transition-colors"
                                    >
                                      Result
                                    </button>
                                  );
                                }
                                return null;
                              })()}
                            </div>
                          )}

                        <div className="flex gap-1">
                          <button
                            onClick={() => setIsPanelExpanded(false)}
                            className="p-1.5 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100"
                          >
                            <ChevronDown className="w-4 h-4" />
                          </button>
                        </div>
                      </div>

                      {/* Tabs */}
                      <div className="flex items-center px-4 border-b border-gray-200 bg-gray-50/50">
                        <button
                          onClick={() => setActiveTab("outputs")}
                          className={`px-4 py-2.5 text-xs font-medium transition-colors border-b-2 flex items-center gap-1.5 ${
                            activeTab === "outputs"
                              ? "border-blue-500 text-blue-700 bg-blue-50/50"
                              : "border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-100/50"
                          }`}
                        >
                          <FileUp className="w-3.5 h-3.5" />
                          Outputs
                        </button>
                        <button
                          onClick={() => setActiveTab("inputs")}
                          className={`px-4 py-2.5 text-xs font-medium transition-colors border-b-2 flex items-center gap-1.5 ${
                            activeTab === "inputs"
                              ? "border-blue-500 text-blue-700 bg-blue-50/50"
                              : "border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-100/50"
                          }`}
                        >
                          <FileDown className="w-3.5 h-3.5" />
                          Inputs
                        </button>
                        <button
                          onClick={() => setActiveTab("logs")}
                          className={`px-4 py-2.5 text-xs font-medium transition-colors border-b-2 flex items-center gap-1.5 ${
                            activeTab === "logs"
                              ? "border-blue-500 text-blue-700 bg-blue-50/50"
                              : "border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-100/50"
                          }`}
                        >
                          <FileText className="w-3.5 h-3.5" />
                          Logs & Meta
                        </button>
                        {getTokenUsage() && (
                          <button
                            onClick={() => setActiveTab("usage")}
                            className={`px-4 py-2.5 text-xs font-medium transition-colors border-b-2 flex items-center gap-1.5 ${
                              activeTab === "usage"
                                ? "border-amber-500 text-amber-700 bg-amber-50/50"
                                : "border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-100/50"
                            }`}
                          >
                            <Cpu className="w-3.5 h-3.5" />
                            Token Analytics
                          </button>
                        )}
                        {getActiveNodeData().executionStatus === "error" && (
                          <button
                            onClick={() => setActiveTab("error")}
                            className={`px-4 py-2.5 text-xs font-medium transition-colors border-b-2 flex items-center gap-1.5 ${
                              activeTab === "error"
                                ? "border-rose-500 text-rose-700 bg-rose-50/50"
                                : "border-transparent text-rose-600 hover:text-rose-900 hover:bg-rose-50/50"
                            }`}
                          >
                            <AlertTriangle className="w-3 h-3" />
                            Error
                          </button>
                        )}
                      </div>

                      {/* Content */}
                      <div className="flex-1 overflow-y-auto bg-gray-50/30 p-4">
                        {activeTab === "outputs" && (
                          <div className="space-y-4">
                            <JsonViewerContainer
                              key={`outputs-${selectedNode?.id}-${selectedIterationIndex}`}
                              data={getActiveNodeData().outputs || {}}
                              title="OUTPUT JSON"
                            />
                          </div>
                        )}

                        {activeTab === "inputs" && (
                          <div className="space-y-4">
                            <JsonViewerContainer
                              key={`inputs-${selectedNode?.id}-${selectedIterationIndex}`}
                              data={getActiveNodeData().inputs || {}}
                              title="INPUT JSON"
                            />
                          </div>
                        )}

                        {activeTab === "logs" && (
                          <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-3">
                              <div className="bg-white p-3 rounded-lg border border-gray-200 shadow-sm">
                                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1">
                                  Status
                                </span>
                                <div className="flex items-center gap-2">
                                  <div
                                    className={`w-2 h-2 rounded-full ${
                                      getActiveNodeData()?.executionStatus ===
                                      "success"
                                        ? "bg-green-500"
                                        : getActiveNodeData()
                                              ?.executionStatus === "error"
                                          ? "bg-red-500"
                                          : "bg-blue-500"
                                    }`}
                                  />
                                  <span className="font-mono text-sm text-gray-700 font-medium">
                                    {(
                                      getActiveNodeData()?.executionStatus ||
                                      "UNKNOWN"
                                    ).toUpperCase()}
                                  </span>
                                </div>
                              </div>

                              <div className="bg-white p-3 rounded-lg border border-gray-200 shadow-sm">
                                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1">
                                  Duration
                                </span>
                                <div className="flex items-center gap-2 text-gray-700">
                                  <Clock className="w-3.5 h-3.5 text-gray-400" />
                                  <span className="font-mono text-sm">
                                    {getActiveNodeData()?.timeTaken !==
                                      undefined &&
                                    getActiveNodeData()?.timeTaken !== null
                                      ? `${Number(
                                          getActiveNodeData().timeTaken,
                                        ).toFixed(4)}s`
                                      : "N/A"}
                                  </span>
                                </div>
                              </div>

                              <div className="bg-white p-3 rounded-lg border border-gray-200 shadow-sm">
                                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1">
                                  Started At
                                </span>
                                <div className="flex items-center gap-2 text-gray-700">
                                  <Calendar className="w-3.5 h-3.5 text-gray-400" />
                                  <span className="font-mono text-sm">
                                    {getActiveNodeData()?.startTime
                                      ? formatDate(
                                          getActiveNodeData().startTime,
                                        )
                                      : "-"}
                                  </span>
                                </div>
                              </div>

                              <div className="bg-white p-3 rounded-lg border border-gray-200 shadow-sm">
                                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1">
                                  Finished At
                                </span>
                                <div className="flex items-center gap-2 text-gray-700">
                                  <Calendar className="w-3.5 h-3.5 text-gray-400" />
                                  <span className="font-mono text-sm">
                                    {getActiveNodeData()?.endTime
                                      ? formatDate(getActiveNodeData().endTime)
                                      : "-"}
                                  </span>
                                </div>
                              </div>

                              <div className="bg-white p-3 rounded-lg border border-gray-200 shadow-sm col-span-2">
                                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1">
                                  Step ID
                                </span>
                                <div className="flex items-center gap-2 text-gray-700">
                                  <span className="font-mono text-xs text-gray-500 break-all">
                                    {/* We need step ID here. getActiveNodeData returns merged object. 
                                        In getExecutionSteps, we didn't explicitly put 'id' into historicalData, 
                                        but we did put it into the wrapper object.
                                        getActiveNodeData MERGES properties.
                                        Let's check getActiveNodeData definition in previous turn.
                                        It spreads `...activeStep.historicalData`. It merges executionStatus, startTime, endTime, timeTaken explicitly.
                                        It does NOT merge `id` explicitly.
                                        However, `activeStep.id` exists. `ExecutionPanel` has access to `activeStep`.
                                        Use `activeStep` directly? No, `getActiveNodeData` is helper.
                                        I should update `getActiveNodeData` to include `id` if I want consistency, 
                                        OR I can just access `activeStep` (which is in scope of the render function?)
                                        `activeStep` is defined at line 588. The render uses `getActiveNodeData`.
                                        I will trust `activeStep` is available in closure scope since it is defined at component level. 
                                        PROCEEDING with `activeStep?.id`.
                                     */}
                                    {activeStep?.id || "-"}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                        {activeTab === "error" && (
                          <div className="bg-rose-50 rounded-lg border border-rose-100 p-4">
                            <h4 className="text-sm font-semibold text-rose-800 mb-2 flex items-center gap-2">
                              <AlertTriangle className="w-4 h-4" />
                              Error Details
                            </h4>
                            <div className="space-y-3 mt-1">
                              {getActiveNodeData().error && (
                                <div className="bg-white/60 rounded-md p-3 border border-rose-200 shadow-sm">
                                  <span className="text-[11px] font-bold text-rose-900/80 uppercase tracking-wider mb-2 block">
                                    Message
                                  </span>
                                  <pre className="text-[13px] text-rose-800 whitespace-pre-wrap font-mono leading-relaxed overflow-x-auto max-h-96 overflow-y-auto">
                                    {(() => {
                                      const err = getActiveNodeData().error;
                                      if (typeof err === "string") {
                                        try {
                                          return JSON.stringify(
                                            JSON.parse(err),
                                            null,
                                            2,
                                          );
                                        } catch (e) {
                                          const jsonMatch =
                                            err.match(/\{.*\}/s);
                                          if (jsonMatch) {
                                            try {
                                              const parsed = JSON.parse(
                                                jsonMatch[0],
                                              );
                                              const prefix = err
                                                .substring(0, jsonMatch.index)
                                                .trim();
                                              return prefix
                                                ? `${prefix}\n\n${JSON.stringify(parsed, null, 2)}`
                                                : JSON.stringify(
                                                    parsed,
                                                    null,
                                                    2,
                                                  );
                                            } catch (e2) {
                                              return err;
                                            }
                                          }
                                          return err;
                                        }
                                      }
                                      return JSON.stringify(err, null, 2);
                                    })()}
                                  </pre>
                                </div>
                              )}

                              {getActiveNodeData().error_description && (
                                <div className="bg-white/60 rounded-md p-3 border border-rose-200 shadow-sm">
                                  <span className="text-[11px] font-bold text-rose-900/80 uppercase tracking-wider mb-2 block">
                                    Description
                                  </span>
                                  <p className="text-[13px] text-rose-800 whitespace-pre-wrap font-medium leading-relaxed">
                                    {getActiveNodeData().error_description}
                                  </p>
                                </div>
                              )}

                              {getActiveNodeData().error_details && (
                                <div className="bg-white/60 rounded-md p-3 border border-rose-200 shadow-sm">
                                  <span className="text-[11px] font-bold text-rose-900/80 uppercase tracking-wider mb-2 block">
                                    Details
                                  </span>
                                  <pre className="text-[13px] text-rose-800 whitespace-pre-wrap font-mono leading-relaxed overflow-x-auto max-h-48 overflow-y-auto">
                                    {JSON.stringify(
                                      getActiveNodeData().error_details,
                                      null,
                                      2,
                                    )}
                                  </pre>
                                </div>
                              )}

                              {!getActiveNodeData().error &&
                                !getActiveNodeData().error_description &&
                                !getActiveNodeData().error_details && (
                                  <div className="bg-white/60 rounded-md p-3 border border-rose-200 shadow-sm text-center">
                                    <span className="text-[13px] text-rose-700/80 italic font-medium">
                                      Unknown error
                                    </span>
                                  </div>
                                )}
                            </div>
                          </div>
                        )}

                        {activeTab === "usage" &&
                          (() => {
                            const usage = getTokenUsage();
                            if (!usage) return null;
                            const { totals, perNode } = usage;

                            return (
                              <div className="space-y-4">
                                {/* Summary Cards */}
                                <div className="grid grid-cols-3 gap-3">
                                  {/* Total Tokens Card */}
                                  <div className="relative overflow-hidden bg-gradient-to-br from-violet-50 via-purple-50 to-fuchsia-50 p-4 rounded-xl border border-violet-200/60 shadow-sm">
                                    <div className="absolute top-0 right-0 w-20 h-20 bg-violet-200/20 rounded-full -translate-y-1/2 translate-x-1/2" />
                                    <div className="relative">
                                      <div className="flex items-center gap-2 mb-2">
                                        <div className="w-7 h-7 rounded-lg bg-violet-100 border border-violet-200 flex items-center justify-center">
                                          <Cpu className="w-3.5 h-3.5 text-violet-700" />
                                        </div>
                                        <span className="text-[10px] font-bold text-violet-600/80 uppercase tracking-wider">
                                          Total Tokens
                                        </span>
                                      </div>
                                      <p className="text-xl font-bold text-violet-900 tracking-tight">
                                        {formatTokenCount(totals.total_tokens)}
                                      </p>
                                      <p className="text-[10px] text-violet-600/70 mt-0.5 font-medium">
                                        {totals.total_tokens.toLocaleString(
                                          "en-IN",
                                        )}{" "}
                                        tokens
                                      </p>
                                    </div>
                                  </div>

                                  {/* Prompt Tokens Card */}
                                  <div className="relative overflow-hidden bg-gradient-to-br from-sky-50 via-cyan-50 to-blue-50 p-4 rounded-xl border border-sky-200/60 shadow-sm">
                                    <div className="absolute top-0 right-0 w-20 h-20 bg-sky-200/20 rounded-full -translate-y-1/2 translate-x-1/2" />
                                    <div className="relative">
                                      <div className="flex items-center gap-2 mb-2">
                                        <div className="w-7 h-7 rounded-lg bg-sky-100 border border-sky-200 flex items-center justify-center">
                                          <ArrowUpRight className="w-3.5 h-3.5 text-sky-700" />
                                        </div>
                                        <span className="text-[10px] font-bold text-sky-600/80 uppercase tracking-wider">
                                          Prompt
                                        </span>
                                      </div>
                                      <p className="text-xl font-bold text-sky-900 tracking-tight">
                                        {formatTokenCount(totals.prompt_tokens)}
                                      </p>
                                      <p className="text-[10px] text-sky-600/70 mt-0.5 font-medium">
                                        {totals.prompt_tokens.toLocaleString(
                                          "en-IN",
                                        )}{" "}
                                        tokens
                                      </p>
                                    </div>
                                  </div>

                                  {/* Completion Tokens Card */}
                                  <div className="relative overflow-hidden bg-gradient-to-br from-emerald-50 via-green-50 to-teal-50 p-4 rounded-xl border border-emerald-200/60 shadow-sm">
                                    <div className="absolute top-0 right-0 w-20 h-20 bg-emerald-200/20 rounded-full -translate-y-1/2 translate-x-1/2" />
                                    <div className="relative">
                                      <div className="flex items-center gap-2 mb-2">
                                        <div className="w-7 h-7 rounded-lg bg-emerald-100 border border-emerald-200 flex items-center justify-center">
                                          <ArrowDownRight className="w-3.5 h-3.5 text-emerald-700" />
                                        </div>
                                        <span className="text-[10px] font-bold text-emerald-600/80 uppercase tracking-wider">
                                          Completion
                                        </span>
                                      </div>
                                      <p className="text-xl font-bold text-emerald-900 tracking-tight">
                                        {formatTokenCount(
                                          totals.completion_tokens,
                                        )}
                                      </p>
                                      <p className="text-[10px] text-emerald-600/70 mt-0.5 font-medium">
                                        {totals.completion_tokens.toLocaleString(
                                          "en-IN",
                                        )}{" "}
                                        tokens
                                      </p>
                                    </div>
                                  </div>
                                </div>

                                {/* Per-Node Breakdown Table */}
                                <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                                  <div className="px-4 py-3 bg-gradient-to-r from-gray-50 to-slate-50 border-b border-gray-200 flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                      <BarChart3 className="w-4 h-4 text-gray-500" />
                                      <span className="text-xs font-bold text-gray-700 uppercase tracking-wide">
                                        Per-Node Breakdown
                                      </span>
                                    </div>
                                    <span className="text-[10px] text-gray-400 font-medium bg-gray-100 px-2 py-0.5 rounded-full border border-gray-200">
                                      {perNode.length}{" "}
                                      {perNode.length === 1 ? "node" : "nodes"}
                                    </span>
                                  </div>

                                  <div className="divide-y divide-gray-100">
                                    {/* Table Header */}
                                    <div className="grid grid-cols-10 gap-2 px-4 py-2.5 bg-gray-50/80">
                                      <div className="col-span-4 text-[10px] font-bold text-gray-500 uppercase tracking-wider">
                                        Node
                                      </div>
                                      <div className="col-span-2 text-[10px] font-bold text-gray-500 uppercase tracking-wider text-right">
                                        Prompt
                                      </div>
                                      <div className="col-span-2 text-[10px] font-bold text-gray-500 uppercase tracking-wider text-right">
                                        Completion
                                      </div>
                                      <div className="col-span-2 text-[10px] font-bold text-gray-500 uppercase tracking-wider text-right">
                                        Total
                                      </div>
                                    </div>

                                    {/* Table Rows */}
                                    {perNode.map((u: any, idx: number) => {
                                      const pct =
                                        totals.total_tokens > 0
                                          ? ((u.total_tokens || 0) /
                                              totals.total_tokens) *
                                            100
                                          : 0;

                                      return (
                                        <div
                                          key={`${u.node_name}-${idx}`}
                                          className="grid grid-cols-10 gap-2 px-4 py-3 hover:bg-blue-50/30 transition-colors group relative"
                                        >
                                          {/* Usage bar background */}
                                          <div
                                            className="absolute inset-y-0 left-0 bg-gradient-to-r from-violet-50/60 to-transparent transition-all"
                                            style={{
                                              width: `${Math.max(pct, 2)}%`,
                                            }}
                                          />

                                          <div className="col-span-4 relative flex items-center gap-2">
                                            <div className="w-5 h-5 rounded-md bg-violet-100 border border-violet-200 flex items-center justify-center flex-shrink-0">
                                              <Sparkles className="w-3 h-3 text-violet-600" />
                                            </div>
                                            <div className="min-w-0">
                                              <p className="text-xs font-semibold text-gray-800 truncate">
                                                {u.node_name}
                                              </p>
                                              <p className="text-[10px] text-gray-400">
                                                {pct.toFixed(1)}% of total
                                              </p>
                                            </div>
                                          </div>
                                          <div className="col-span-2 relative flex items-center justify-end">
                                            <span className="text-xs font-mono text-sky-700 bg-sky-50 px-2 py-0.5 rounded border border-sky-100">
                                              {formatTokenCount(
                                                u.prompt_tokens || 0,
                                              )}
                                            </span>
                                          </div>
                                          <div className="col-span-2 relative flex items-center justify-end">
                                            <span className="text-xs font-mono text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-100">
                                              {formatTokenCount(
                                                u.completion_tokens || 0,
                                              )}
                                            </span>
                                          </div>
                                          <div className="col-span-2 relative flex items-center justify-end">
                                            <span className="text-xs font-mono font-bold text-violet-800">
                                              {formatTokenCount(
                                                u.total_tokens || 0,
                                              )}
                                            </span>
                                          </div>
                                        </div>
                                      );
                                    })}

                                    {/* Totals Row */}
                                    <div className="grid grid-cols-10 gap-2 px-4 py-3 bg-gradient-to-r from-gray-50 to-slate-50 border-t border-gray-200">
                                      <div className="col-span-4 flex items-center gap-2">
                                        <span className="text-xs font-bold text-gray-700 uppercase">
                                          Grand Total
                                        </span>
                                      </div>
                                      <div className="col-span-2 flex items-center justify-end">
                                        <span className="text-xs font-mono font-bold text-sky-800">
                                          {formatTokenCount(
                                            totals.prompt_tokens,
                                          )}
                                        </span>
                                      </div>
                                      <div className="col-span-2 flex items-center justify-end">
                                        <span className="text-xs font-mono font-bold text-emerald-800">
                                          {formatTokenCount(
                                            totals.completion_tokens,
                                          )}
                                        </span>
                                      </div>
                                      <div className="col-span-2 flex items-center justify-end">
                                        <span className="text-xs font-mono font-bold text-violet-900">
                                          {formatTokenCount(
                                            totals.total_tokens,
                                          )}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })()}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center h-full text-gray-400">
            <MousePointerClick className="w-16 h-16 mb-4 opacity-20" />
            <p className="text-lg font-medium">Select an execution</p>
            <p className="text-sm text-gray-400">
              Click on an execution from the sidebar to view details
            </p>
          </div>
        )}
      </div>

      {showTokenAnalytics && fullExecutionData && (
        <ExecutionTokenAnalytics
          executionData={fullExecutionData}
          workflowName={workflowName}
          onClose={() => setShowTokenAnalytics(false)}
        />
      )}
    </div>
  );
};

export default ExecutionsPanel;
